import type { GridProps } from '@mui/material';
import type { ActivityDTO, InspectorDTO, ActivityCompetencyDTO } from 'src/types/dto';

import dayjs from 'dayjs';
import React, { useState } from 'react';
import utc from 'dayjs/plugin/utc';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  Typography,
  Autocomplete,
  Collapse,
} from '@mui/material';

import { spacingUtil } from 'src/utils/spacing-util';
import {
  fGetMonthsInRange,
  fDateRangeShortLabel,
} from 'src/utils/format-time';
import { formatNumberToMaxPlaces } from 'src/utils/decimal-places';

import { Label } from 'src/components/label';
import { TagsList } from 'src/components/tags-list';
import { StaticMap } from 'src/components/static-map';
import { Iconify } from 'src/components/iconify';
import { getCompetencyName } from 'src/utils/competency';

dayjs.extend(utc);

const GridItem = (props: GridProps & { children: any }) => (
  <Grid item {...props} sx={{ display: 'flex' }}>
    {props?.children}
  </Grid>
);

const standardFieldSx = {
  flex: 1,
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: (theme: { palette: { text: { primary: string } } }) =>
      theme.palette.text.primary,
  },
};

export const LeftHandSide = ({
  activity,
  activeCompetencies,
  selectedMonth,
  onMonthSelect,
  inspectors,
  onInspectorSelect,
  highlightedMarkerId,
  selectedIds,
  coveredDurationDays = 0,
  totalDurationDays = 0,
}: {
  activity: ActivityDTO;
  activeCompetencies: { activityCompetency: ActivityCompetencyDTO; isActive: boolean }[];
  selectedMonth: string;
  onMonthSelect: (month: string) => void;
  inspectors: InspectorDTO[];
  onInspectorSelect: (inspectorId: number) => void;
  highlightedMarkerId?: number;
  selectedIds?: number[];
  coveredDurationDays?: number;
  totalDurationDays?: number;
}) => {
  const { t } = useTranslation('activities');
  const [showMore, setShowMore] = useState(false);

  const durationComplete =
    totalDurationDays > 0 && coveredDurationDays + 0.0001 >= totalDurationDays;
  const competenciesComplete =
    (activeCompetencies?.length ?? 0) > 0 && activeCompetencies.every((ac) => ac.isActive);

  const windowLabel = activity?.startDate
    ? fDateRangeShortLabel(activity?.startDate, activity?.dueDate)
    : fDateRangeShortLabel(dayjs().add(1, 'day').toDate(), activity?.dueDate);

  const durationProgressLabel = t('allocation.details.activity.durationProgress', {
    covered: formatNumberToMaxPlaces(coveredDurationDays, 5),
    total: formatNumberToMaxPlaces(totalDurationDays, 5),
  });

  return (
    <Stack margin={spacingUtil(2)} ml="0" gap={spacingUtil(2)} maxHeight="100%">
      <Typography variant="h6">{t('allocation.details.requirements.title')}</Typography>

      <TextField
        label={t('allocation.details.requirements.window')}
        variant="standard"
        disabled
        fullWidth
        value={windowLabel}
        sx={standardFieldSx}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
        <TextField
          label={t('allocation.details.activity.duration')}
          variant="standard"
          fullWidth
          disabled
          value={durationProgressLabel}
          sx={standardFieldSx}
        />
        {durationComplete ? (
          <Iconify
            icon="solar:check-circle-bold"
            width={18}
            sx={{ color: 'success.main', flexShrink: 0, mt: 1 }}
          />
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1, fontWeight: 600 }}
          >
            {t('allocation.details.requirements.competencies')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacingUtil(1) }}>
            {activeCompetencies?.map((ac) => (
              <Label
                key={ac.activityCompetency.competency.id}
                variant={ac.isActive ? 'filled' : 'soft'}
                color={ac.isActive ? 'info' : 'default'}
              >
                {getCompetencyName(ac.activityCompetency.competency)}
              </Label>
            ))}
          </Box>
        </Box>
        {competenciesComplete ? (
          <Iconify
            icon="solar:check-circle-bold"
            width={18}
            sx={{ color: 'success.main', flexShrink: 0 }}
          />
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => setShowMore((prev) => !prev)}
          endIcon={
            <Iconify
              icon={showMore ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'}
              width={18}
            />
          }
          sx={{ color: 'text.secondary', textTransform: 'none' }}
        >
          {showMore
            ? t('allocation.details.requirements.showLess')
            : t('allocation.details.requirements.showMore')}
        </Button>
      </Box>

      <Collapse in={showMore}>
        <Stack gap={spacingUtil(2)} sx={{ mb: spacingUtil(1) }}>
          <Autocomplete
            fullWidth
            value={selectedMonth ? dayjs.utc(selectedMonth).startOf('month').toDate() : null}
            onChange={(_, newValue) =>
              onMonthSelect(newValue ? dayjs.utc(newValue).format('YYYY-MM') : '')
            }
            options={
              activity.standard?.windowSizeType === 'YEAR'
                ? fGetMonthsInRange(activity.dueDate, { years: activity.standard?.windowSize }, true)
                : fGetMonthsInRange(activity.dueDate, { months: activity.standard?.windowSize }, true)
            }
            getOptionLabel={(option) => (option ? dayjs.utc(option).format('MMMM YYYY') : '')}
            renderInput={(params) => (
              <TextField {...params} label={t('allocation.details.activity.targetMonth')} />
            )}
          />
          <Grid container spacing={spacingUtil(1)}>
            <GridItem xs={12}>
              <TextField
                label={t('allocation.details.activity.type')}
                fullWidth
                disabled
                value={activity.type}
              />
            </GridItem>
          </Grid>
          <TextField
            label={t('allocation.details.activity.site')}
            fullWidth
            disabled
            value={
              activity.site?.address?.trim()
                ? activity.site.address
                : [
                    activity.site?.zipCode?.trim(),
                    activity.site?.city?.trim(),
                    activity.site?.country?.trim(),
                  ]
                    .filter(Boolean)
                    .join(', ')
            }
          />
          {activity.accreditationBodies && activity.accreditationBodies.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('allocation.details.requirements.accreditationBodies')}
              </Typography>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacingUtil(1) }}>
                {activity.accreditationBodies?.map((ab) => (
                  <Label key={ab.id} variant="soft" color="default">
                    {ab.name}
                  </Label>
                ))}
              </div>
            </Box>
          )}
          {activity.minimumRole && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('allocation.details.requirements.minimumRole')}
              </Typography>
              <Label key={activity.minimumRole?.id} variant="soft" color="default">
                {activity.minimumRole?.name}
              </Label>
            </Box>
          )}
        </Stack>
      </Collapse>

      <StaticMap
        location={activity.site?.location}
        inspectors={inspectors}
        onInspectorSelect={onInspectorSelect}
        highlightedMarkerId={highlightedMarkerId}
        selectedIds={selectedIds}
      />
    </Stack>
  );
};
